// Copyright (c) 2026. CYBERKOALA.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import { useCallback, useEffect, useRef, useState } from 'react';
import { DegradationPreference, Pad, Session, Status, Stats, VideoCodec } from './session';
import './styles.css';
import { isPardusBrowser } from '@/components/check-user-agent';
import {
  CheckIcon,
  CopyIcon,
  CrownIcon,
  Gamepad2Icon,
  Maximize2Icon,
  Minimize2Icon,
  MonitorPlayIcon,
  MonitorStopIcon,
  XIcon,
} from 'lucide-react';
import { ExitIcon } from '@radix-ui/react-icons';
import { UserPlan } from '@/models';
import { MdOutlinePreview } from 'react-icons/md';
import { ScreenShareIcon, ScreenShareStopIcon } from '@livekit/components-react';

const SLOTS = 4;
const ROOM_RE = /^[A-Za-z0-9_-]{3,64}$/;
const newRoom = () => Math.random().toString(36).slice(2, 8);
const empty = () => Array<string | null>(SLOTS).fill(null);

const FATAL: Record<string, string> = {
  'host-exists': 'Уже есть хост.',
  'room-full': 'Заполнено.',
  'invalid-room': 'Название лобби состоит из 3–64 букв, цифр или символов "-" или "_".',
  origin: 'Этот источник не разрешён сервером.',
  rate: 'Слишком много сообщений; соединение закрыто.',
};

const STATUS: Record<Status, string> = {
  connecting: 'Подключение...',
  connected: 'Подключено',
  reconnecting: 'Переподключение...',
  'host-left': 'Ожидание хоста...',
};

export interface RemotePlayOverlayProps {
  onLeave?: () => void;
  plan?: UserPlan | null;
  className?: string;
}

export function RemotePlayOverlay({
  onLeave,
  plan = UserPlan.FREE,
  className = '',
}: RemotePlayOverlayProps) {
  const [room, setRoom] = useState(() => {
    // Try to get room from URL hash first
    const hash = window.location.hash.slice(1);
    return hash || newRoom();
  });
  const [mode, setMode] = useState<'idle' | 'host' | 'guest'>('idle');
  const [status, setStatus] = useState<Status>('connecting');
  const [error, setError] = useState('');
  const [slots, setSlots] = useState(empty());
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [pads, setPads] = useState(0);
  const [copied, setCopied] = useState(false);
  const [quality, setQuality] = useState<'720' | '1080'>('720');
  const [degradationPreference, setDegradationPreference] = useState<DegradationPreference>(() => {
    try {
      const saved = localStorage.getItem('rp.degradationPreference');
      if (saved === 'balanced' || saved === 'maintain-resolution' || saved === 'maintain-framerate')
        return saved;
    } catch {
      /* storage unavailable */
    }
    return 'maintain-framerate';
  });
  const [codec, setCodec] = useState<VideoCodec>('H264');
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState<Stats>({});
  const [isExpanded, setIsExpanded] = useState(false);
  const fullscreenRequested = useRef(false);
  const [video, setVideo] = useState<boolean>(() => {
    try {
      return localStorage.getItem('rp.video') !== '0';
    } catch {
      return true;
    }
  });
  const videoEl = useRef<HTMLVideoElement>(null);
  const [localPreviewStream, setLocalPreviewStream] = useState<MediaStream | null>(null);
  const [showHostPreview, setShowHostPreview] = useState(false);
  const hostPreviewEl = useRef<HTMLVideoElement>(null);
  const sess = useRef<Session | null>(null);
  const map = useRef(new Map<string, number>()); // "peer:padIndex" -> virtual slot
  const isPaid = plan !== UserPlan.FREE;

  const playerColors = ['text-red-500', 'text-blue-500', 'text-green-500', 'text-amber-400'];

  const refresh = () => {
    const a = empty();
    map.current.forEach((s, k) => (a[s] = k));
    setSlots(a);
  };

  const leave = useCallback(() => {
    sess.current?.stop();
    sess.current = null;
    // Free all slots
    map.current.forEach((s) => window.gamepadAPI?.free(s));
    map.current.clear();
    setSlots(empty());
    setStream(null);
    setLocalPreviewStream(null);
    setShowHostPreview(false);
    setMode('idle');
    setStatus('connecting');
    onLeave?.();
  }, [onLeave]);

  /** Host: screen capture on/off. Guest: receive video on/off. Off = gamepad-only (no video or audio traffic at all). */
  async function toggleVideo(on: boolean) {
    setError('');
    try {
      await sess.current?.setVideo(on); // no session yet (idle screen): just remember the choice
      setVideo(on);
      try {
        localStorage.setItem('rp.video', on ? '1' : '0');
      } catch {
        /* storage unavailable */
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const enterGuestFullscreen = useCallback(async () => {
    const el = videoEl.current;

    if (!el || document.fullscreenElement || fullscreenRequested.current) {
      return;
    }

    fullscreenRequested.current = true;

    try {
      await el.requestFullscreen();
    } catch (error) {
      fullscreenRequested.current = false;
      console.warn('[Play Together] Automatic fullscreen rejected:', error);
    }
  }, []);

  async function go(m: 'host' | 'guest') {
    setError('');
    const s = new Session(
      room,
      m === 'host',
      {
        onStream: setStream,
        onStatus: setStatus,
        onEnded: () => setVideo(false), // capture ended outside the app: stay hosting in gamepad-only mode
        onFatal: (r) => {
          setError(FATAL[r] ?? `Отключено (${r})`);
          leave();
        },
        onPad: (peer, p: Pad) => {
          const key = `${peer}:${p.i}`;
          let slot = map.current.get(key);
          if (slot === undefined) {
            const used = [...map.current.values()];
            slot = [0, 1, 2, 3].find((n) => !used.includes(n));
            if (slot === undefined) return; // 4 virtual pads max
            map.current.set(key, slot);
            refresh();
          }
          window.gamepadAPI?.pad(slot, p);
        },
        onLeave: (peer) => {
          [...map.current]
            .filter(([k]) => k.startsWith(peer + ':'))
            .forEach(([k, slot]) => {
              map.current.delete(k);
              window.gamepadAPI?.free(slot);
            });
          refresh();
        },
        onLocalStream: (s) => {
          setLocalPreviewStream(s);
          setShowHostPreview(!!s);
        },
      },
      video,
      quality,
      degradationPreference,
      isPaid ? codec : 'H264'
    );
    sess.current = s;
    try {
      await s.start();
      history.replaceState(null, '', '#' + room);
      setMode(m);
    } catch (e) {
      sess.current = null;
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    if (videoEl.current) videoEl.current.srcObject = stream;
  }, [stream, mode]);
  useEffect(() => () => sess.current?.stop(), []);

  useEffect(() => {
    // latency diagnostics once per second
    if (!showStats || mode === 'idle') return;
    const id = setInterval(() => {
      sess.current
        ?.getStats()
        .then(setStats)
        .catch(() => {});
    }, 1000);
    return () => clearInterval(id);
  }, [showStats, mode]);

  useEffect(() => {
    const el = hostPreviewEl.current;
    if (!el) return;

    el.srcObject = localPreviewStream;

    return () => {
      el.srcObject = null;
    };
  }, [localPreviewStream, showHostPreview]);

  // Guest: enter native fullscreen when video starts playing and a gamepad is connected.
  useEffect(() => {
    if (mode !== 'guest' || !video || !stream || pads === 0) {
      return;
    }

    const el = videoEl.current;
    if (!el) return;

    const handlePlaying = () => {
      void enterGuestFullscreen();
    };

    if (!el.paused && el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      handlePlaying();
    } else {
      el.addEventListener('playing', handlePlaying);
    }

    return () => {
      el.removeEventListener('playing', handlePlaying);
    };
  }, [mode, video, stream, pads, enterGuestFullscreen]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        fullscreenRequested.current = false;
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    if (mode !== 'guest' || !video || !stream) {
      fullscreenRequested.current = false;

      if (document.fullscreenElement === videoEl.current) {
        void document.exitFullscreen();
      }
    }
  }, [mode, video, stream]);

  // Guest: poll controllers; send on change plus a 100 ms keepalive (the channel is lossy)
  useEffect(() => {
    if (mode !== 'guest') return;
    const last = new Map<number, { k: string; t: number }>();
    const id = setInterval(() => {
      const list = [...navigator.getGamepads()].filter(Boolean) as Gamepad[];
      setPads(list.length);
      for (const gp of list) {
        const p: Pad = {
          i: gp.index,
          b: gp.buttons.map((b) => +b.value.toFixed(2)),
          a: gp.axes.map((x) => +x.toFixed(2)),
        };
        const k = JSON.stringify(p),
          prev = last.get(gp.index),
          now = performance.now();
        if (!prev || prev.k !== k || now - prev.t > 100) {
          sess.current?.sendPad(p);
          last.set(gp.index, { k, t: now });
        }
      }
    }, 8);
    const off = (e: GamepadEvent) => {
      sess.current?.sendPad({ i: e.gamepad.index, b: [], a: [] });
      last.delete(e.gamepad.index);
    };
    window.addEventListener('gamepaddisconnected', off);
    return () => {
      clearInterval(id);
      window.removeEventListener('gamepaddisconnected', off);
    };
  }, [mode]);

  const inviteBase: string = (window as any).__RP_CONFIG__?.inviteBase ?? location.origin; // desktop app supplies a LAN-reachable base
  const link = `${inviteBase}${location.pathname}#${room}`;
  const copy = async () => {
    try {
      if (window.electronAPI?.copyToClipboard) {
        await window.electronAPI.copyToClipboard(link);
      } else {
        await navigator.clipboard.writeText(link);
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };
  const validRoom = ROOM_RE.test(room);

  // If not expanded, show a restore button
  if (!isExpanded) {
    return (
      <div className={`remote-play-mini   ${className}`}>
        <button
          className="remote-play-restore-btn"
          onClick={() => setIsExpanded(true)}
          title="Развернуть Play Together"
        >
          <Gamepad2Icon className="w-5 h-5 text-white" />
          <span className="text-white">Играем вместе</span>
        </button>
      </div>
    );
  }

  const isHost = mode === 'host';
  const isGuest = mode === 'guest';
  const canHost = isPardusBrowser();

  return (
    <div className={`remote-play-overlay ${className}`}>
      <div className="remote-play-header">
        <div className="remote-play-title">
          <Gamepad2Icon className="w-5 h-5 text-white" />
          <span className="text-white">Играем вместе</span>
        </div>
        <div className="remote-play-actions">
          {mode !== 'idle' && isPaid && (
            <button className="ghost-btn" onClick={() => setShowStats((v) => !v)}>
              Статы
            </button>
          )}
          {mode !== 'idle' && (
            <button className="ghost-btn" onClick={leave} title="Выйти">
              <ExitIcon className="w-3.5 h-3.5 text-white" />
            </button>
          )}
          <button className="ghost-btn" onClick={() => setIsExpanded(false)} title="Скрыть">
            <Minimize2Icon className="w-3.5 h-3.5 text-white" />
          </button>
        </div>
      </div>
      {showStats && mode !== 'idle' && isPaid && (
        <div className="card stats">
          Codec: {stats.codec ?? '?'} · RTT {stats.rtt ?? '?'} ms · path {stats.path ?? '?'}
          <br />
          {stats.res ?? '?'} @ {stats.fps ?? '?'} fps · {stats.kbps ?? '?'} kbps · lost{' '}
          {stats.lost ?? 0}
          <br />
          {mode === 'guest'
            ? `jitter buffer ${stats.jitterBufferMs ?? '?'} ms · decode ${stats.decodeMs ?? '?'} ms`
            : `encode ${stats.encodeMs ?? '?'} ms · limited by ${stats.limit ?? '?'}`}
        </div>
      )}
      {plan == UserPlan.FREE && (
        <div className="rp-paid-badge">
          <CrownIcon className="w-6 h-6" />
          <span>
            Вы тестируете платную функцию, которая будет доступна в рамках тарифа BASIC и выше
          </span>
        </div>
      )}

      {error && <div className="rp-card rp-err rp-sm">{error}</div>}

      {mode === 'idle' && (
        <div className="rp-card rp-sm">
          <div className="rp-row">
            <input
              value={room}
              onChange={(e) => setRoom(e.target.value.trim())}
              aria-label="Код"
              placeholder="Код"
              className="font-mono"
            />
          </div>
          <div className="rp-row rp-sm-row">
            {canHost && (
              <button disabled={!validRoom} onClick={() => go('host')}>
                Создать
              </button>
            )}
            <button className="ghost-btn" disabled={!validRoom} onClick={() => go('guest')}>
              Войти
            </button>
            {isPaid && (
              <select
                className="rp-select"
                value={quality}
                onChange={(e) => setQuality(e.target.value as '720' | '1080')}
                aria-label="Разрешение видео"
              >
                <option value="1080">1080p60</option>
                <option value="720">720p60</option>
              </select>
            )}
            {isPaid && (
              <select
                className="rp-select"
                value={codec}
                onChange={(e) => setCodec(e.target.value as VideoCodec)}
                aria-label="Видеокодек"
                title="Выберите кодек"
              >
                <option value="H264">CAVT</option>
                <option value="VP8">VP8</option>
                <option value="VP9">VP9</option>
                <option value="AV1">AV1</option>
              </select>
            )}
            <select
              className="rp-select"
              value={degradationPreference}
              onChange={(e) => {
                const value = e.target.value as DegradationPreference;
                setDegradationPreference(value);
                try {
                  localStorage.setItem('rp.degradationPreference', value);
                } catch {
                  /* storage unavailable */
                }
              }}
              aria-label="Режим адаптации качества видео"
              title="Выберите, что сохранять при нехватке пропускной способности или ресурсов"
            >
              <option value="maintain-framerate">Производительность</option>
              <option value="balanced">Баланс</option>
              <option value="maintain-resolution">Качество</option>
            </select>
          </div>
          {!validRoom && <p className="rp-err rp-sm-text">3–64 буквы, цифры, "-" или "_".</p>}
          {/* <p className="rp-mut rp-sm-text">
            {canHost
              ? 'Гости подключаются и используют контроллер.'
              : 'Подключитесь к комнате хоста для совместной игры.'}
          </p> */}
        </div>
      )}

      {mode === 'host' && (
        <div className="rp-card rp-sm">
          <div className="rp-row">
            <span className="rp-sm-label">
              Лобби <b className="font-mono">{room}</b>
            </span>
            <span className={status === 'connected' ? 'rp-ok' : 'rp-mut'}>{STATUS[status]}</span>
            <button className="ghost-btn" onClick={copy}>
              {copied ? <CheckIcon className="size-5" /> : <CopyIcon className="size-5" />}
              {copied ? 'OK' : ''}
            </button>
            <button className="ghost-btn" onClick={() => void toggleVideo(!video)}>
              {video ? (
                <ScreenShareStopIcon className="size-5 text-red-500" />
              ) : (
                <ScreenShareIcon className="size-5" />
              )}
            </button>
            {video && localPreviewStream && (
              <button
                className="ghost-btn"
                onClick={() => setShowHostPreview((v) => !v)}
                aria-pressed={showHostPreview}
                title="Показать или скрыть локальный предпросмотр захвата"
              >
                {showHostPreview ? (
                  <MdOutlinePreview className="size-5 text-red-500" />
                ) : (
                  <MdOutlinePreview className="size-5" />
                )}
              </button>
            )}
          </div>
          {!canHost && (
            <p className="rp-err rp-sm-text">
              Хост в браузере: нельзя создавать виртуальные контроллеры. Для игры используйте
              десктопное приложение.
            </p>
          )}
          {!video && <p className="rp-mut rp-sm-text">Только геймпад.</p>}
          {!window.gamepadAPI && (
            <p className="rp-err rp-sm-text">
              Это браузерный хост: он позволяет транслировать видео, но не может создавать игровые
              контроллеры. Для игры используйте десктопное приложение.
            </p>
          )}
          {showHostPreview && localPreviewStream && (
            <div className="rp-host-preview">
              <div className="rp-host-preview-header">
                <span>Предпросмотр</span>
                <button
                  className="ghost-btn"
                  onClick={() => setShowHostPreview(false)}
                  title="Скрыть предпросмотр"
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              </div>

              <video ref={hostPreviewEl} autoPlay playsInline muted />
            </div>
          )}
          <div className="rp-slots rp-sm-slots">
            {slots.map((s, i) => (
              <div key={i} className={`rp-slot${s ? ' on' : ''}`}>
                <span className="rp-sm-label">
                  <Gamepad2Icon
                    className={`rp-gamepad-icon w-4 h-4 ${playerColors[i % playerColors.length]}`}
                  />
                  {/*<span className="rp-player-number">{i + 1}</span>*/}
                </span>
                <span className="rp-mut rp-sm-text">{s ? s.split(':')[0] : '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === 'guest' && (
        <div className="rp-card rp-sm">
          <div className="rp-row">
            <span className="rp-sm-label">
              Код <b className="font-mono">{room}</b>
            </span>
            <span className={status === 'connected' ? 'rp-ok' : 'rp-mut'}>{STATUS[status]}</span>
            <button className="ghost-btn" onClick={() => void toggleVideo(!video)}>
              {video ? (
                <MonitorStopIcon className="size-5  text-red-500" />
              ) : (
                <MonitorPlayIcon className="size-5" />
              )}
            </button>
            {stream && (
              <button className="ghost-btn" onClick={() => videoEl.current?.requestFullscreen?.()}>
                <Maximize2Icon className="size-5" />
              </button>
            )}
          </div>
          {stream && video ? (
            <div className={`rp-guest-video-stage ${isExpanded ? 'expanded' : ''}`}>
              <video ref={videoEl} autoPlay playsInline muted />

              {/* {isExpanded && (
                <button
                  className="rp-guest-video-collapse ghost-btn"
                  onClick={async () => {
                    if (document.fullscreenElement) {
                      await document.exitFullscreen();
                    }

                    setIsExpanded(false);
                    fullscreenRequested.current = false;
                  }}
                  title="Свернуть видео"
                >
                  <Minimize2Icon className="w-5 h-5" />
                </button>
              )} */}
            </div>
          ) : (
            <p className="rp-mut rp-sm-text">
              {video ? 'Хост не транслирует видео.' : 'Только геймпад.'}
            </p>
          )}
          <p className="rp-mut rp-sm-text">
            Для захвата геймпада необходимо кликнуть в любом месте внутри данной карточки.
          </p>
          <p className="rp-mut rp-sm-text">
            {pads ? `${pads} геймпад(а) отправлен(о) на хост.` : 'Нажмите кнопку на геймпаде.'}
          </p>
        </div>
      )}
    </div>
  );
}
